// Gallery.jsx
import React, { useState, useEffect } from 'react';
import { apiGet } from '../api/client';

import pattern1 from '../images/bengali-motif.svg';

const Gallery = () => {

    const [allArtworks, setAllArtworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeCategory, setActiveCategory] = useState('All');
    const [filteredArtworks, setFilteredArtworks] = useState([]);
    const [selectedArtwork, setSelectedArtwork] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Get unique categories from artworks
    const categories = ['All', ...new Set(allArtworks.map(artwork => artwork.category))];

    useEffect(() => {
        const loadGallery = async () => {
            try {
                const data = await apiGet('/gallery');
                setAllArtworks(data.map(item => ({
                    id: item.id,
                    title: `${item.title}${item.size ? `, ${item.size}` : ''}`,
                    category: item.category,
                    technique: item.technique,
                    year: item.year,
                    image: item.imageUrl,
                    description: item.description
                })));
            } catch (err) {
                setError('Unable to load gallery items right now.');
            } finally {
                setLoading(false);
            }
        };

        loadGallery();
    }, []);

    useEffect(() => {
        // Filter artworks based on selected category
        if (activeCategory === 'All') {
            setFilteredArtworks(allArtworks);
        } else {
            setFilteredArtworks(allArtworks.filter(artwork => artwork.category === activeCategory));
        }
    }, [activeCategory, allArtworks]);

    const handleCategoryChange = (category) => {
        setActiveCategory(category);
    };

    const closeModal = () => {
        setModalOpen(false);
        document.body.style.overflow = 'auto';
    };

    const navigateModal = (direction) => {
        const currentIndex = filteredArtworks.findIndex(artwork => artwork.id === selectedArtwork.id);
        let newIndex;
        if (direction === 'next') {
            newIndex = (currentIndex + 1) % filteredArtworks.length;
        } else {
            newIndex = currentIndex === 0 ? filteredArtworks.length - 1 : currentIndex - 1;
        }
        setSelectedArtwork(filteredArtworks[newIndex]);
    };

    // Close modal on escape key
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, []);

    return (
        <div className="container">
            <div className="section-header" style={{ position: 'relative', textAlign: 'center', marginBottom: '3rem' }}>
                <h2 className="section-title">Artistic Collection</h2>
                <p style={{ maxWidth: '800px', margin: '0 auto', fontSize: '1.1rem' }}>
                    Explore the fusion of folk traditions and modern sensibilities through various mediums and subjects,
                    each telling a unique story at the intersection of mythology and contemporary life.
                </p>

                <div style={{
                    position: 'absolute',
                    bottom: '-20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '10px',
                    backgroundImage: pattern1,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center'
                }}></div>
            </div>

            {/* Category Filters */}
            <div className="gallery-filters" style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '0.8rem',
                margin: '2rem 0 3rem'
            }}>
                {categories.map((category, index) => (
                    <button
                        key={index}
                        className={`filter-btn ${activeCategory === category ? 'active' : ''}`}
                        onClick={() => handleCategoryChange(category)}
                        style={{
                            padding: '0.7rem 1.2rem',
                            backgroundColor: activeCategory === category ? 'var(--primary-color)' : 'transparent',
                            color: activeCategory === category ? 'white' : 'var(--primary-color)',
                            border: '1px solid var(--primary-color)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {category}
                    </button>
                ))}
            </div>

            {/* Loading / Error States */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                    <p>Loading artworks...</p>
                </div>
            )}

            {error && (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                    <p>{error}</p>
                </div>
            )}

            {/* Gallery Grid */}
            {!loading && !error && (
                <div className="gallery-grid">
                    {filteredArtworks.map((artwork) => (
                        <div key={artwork.id} className="gallery-item fade-in">
                            <img src={artwork.image} alt={artwork.title} />
                            <div className="gallery-overlay">
                                <h3>{artwork.title}</h3>
                                <p>{artwork.technique}, {artwork.year}</p>
                                {/* <button className="gallery-btn" onClick={() => openModal(artwork)}>
                                    View Details
                                </button> */}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* No Results */}
            {!loading && !error && filteredArtworks.length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                    <p>No artworks found in this category. Please try another category.</p>
                </div>
            )}

            {/* Artwork Modal */}
            {modalOpen && selectedArtwork && (
                <div className="artwork-modal" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
                        background: 'white',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            width: '100%',
                            height: '5px',
                            background: 'linear-gradient(to right, var(--primary-color), var(--secondary-color))'
                        }}></div>

                        <img src={selectedArtwork.image} alt={selectedArtwork.title} className="modal-image" />

                        <div className="modal-info" style={{ padding: '2rem' }}>
                            <h3 style={{
                                color: 'var(--primary-color)',
                                fontSize: '1.8rem',
                                borderBottom: '1px solid var(--secondary-color)',
                                paddingBottom: '0.5rem',
                                marginBottom: '1rem'
                            }}>{selectedArtwork.title}</h3>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '1.5rem' }}>
                                <div>
                                    <p><strong>Category:</strong> {selectedArtwork.category}</p>
                                    <p><strong>Technique:</strong> {selectedArtwork.technique}</p>
                                </div>
                                <div>
                                    <p><strong>Year:</strong> {selectedArtwork.year}</p>
                                    <p><strong>Artist:</strong> Samir Dutta</p>
                                </div>
                            </div>

                            <p style={{ lineHeight: '1.8' }}>{selectedArtwork.description}</p>

                            <div style={{
                                marginTop: '1.5rem',
                                paddingTop: '1.5rem',
                                borderTop: '1px solid #eee',
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <button className="btn" style={{ background: 'var(--accent-color)' }}>Inquire About This Piece</button>
                                <div>
                                    <button className="btn" style={{
                                        background: 'transparent',
                                        color: 'var(--primary-color)',
                                        border: '1px solid var(--primary-color)',
                                        marginLeft: '0.5rem'
                                    }}>
                                        <i className="fas fa-share-alt"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button className="modal-close" onClick={closeModal} style={{
                            background: 'var(--primary-color)',
                            color: 'white',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            cursor: 'pointer'
                        }}>
                            <i className="fas fa-times"></i>
                        </button>

                        <div className="modal-navigation">
                            <button className="modal-nav-btn" onClick={() => navigateModal('prev')}>
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            <button className="modal-nav-btn" onClick={() => navigateModal('next')}>
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Art Styles Section */}
            < section className="section" >
                <div className="container">
                    <h2 className="section-title">Artistic Techniques</h2>
                    <div className="art-styles-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '2rem',
                        textAlign: 'center'
                    }}>
                        <div className="art-style-item slide-up">
                            <div style={{ fontSize: '3rem', color: 'var(--primary-color)', marginBottom: '1rem' }}>
                                <i className="fas fa-paint-brush"></i>
                            </div>
                            <h3>Water & Oil Colour</h3>
                            <p>Creating bold, vibrant compositions with depth and texture, blending traditional and contemporary elements.</p>
                        </div>
                        <div className="art-style-item slide-up">
                            <div style={{ fontSize: '3rem', color: 'var(--primary-color)', marginBottom: '1rem' }}>
                                <i className="fas fa-feather-alt"></i>
                            </div>
                            <h3>Wash Painting & Tempera</h3>
                            <p>Delicate, flowing techniques that capture the subtlety and movement in narratives inspired by folk traditions.</p>
                        </div>
                        <div className="art-style-item slide-up">
                            <div style={{ fontSize: '3rem', color: 'var(--primary-color)', marginBottom: '1rem' }}>
                                <i className="fas fa-pencil-alt"></i>
                            </div>
                            <h3>Folk Art Interpretation</h3>
                            <p>Reimagining folk motifs with contemporary elements to create a dialogue between tradition and modern life.</p>
                        </div>
                    </div>
                </div>
            </section >


            {/* Gallery Note */}
            <div style={{
                margin: '4rem auto',
                padding: '2rem',
                backgroundColor: 'rgba(227, 180, 72, 0.1)',
                borderRadius: '8px',
                maxWidth: '800px',
                textAlign: 'center',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    top: '-10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--background-color)',
                    padding: '0 1rem',
                    color: 'var(--primary-color)',
                    fontFamily: 'var(--font-primary)'
                }}>Note from the Artist</div>

                <p style={{ fontStyle: 'italic' }}>
                    "Each artwork is a visual story that weaves together traditional folk elements with the texture of modern life.
                    I invite you to look beyond the surface and discover the layers of narrative that connect our past, present, and future."
                </p>
                <p style={{ marginTop: '1rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                    — Samir Dutta
                </p>
            </div>
        </div>
    );
};

export default Gallery;